import * as React from "react"
import {
  type Category,
  type CategoryState,
  type CategorySubtype,
  type CategoryType,
  type EntityCategory,
  ObjectKind,
} from "@digits-graphql/frontend/graphql-bearer"
import { svgPathStyles } from "@digits-shared/components/SVG/svgIconStyles"
import { SvgChevronDown } from "@digits-shared/components/SVGIcons/line/ChevronDown.svg"
import { SvgTag01 } from "@digits-shared/components/SVGIcons/line/Tag01.svg"
import { SvgStars01Solid } from "@digits-shared/components/SVGIcons/solid/Stars01Solid.svg"
import { SvgAIFillGradient } from "@digits-shared/components/SVGIcons/SvgAIGradient"
import {
  AI_GRADIENT_BORDER,
  AI_GRADIENT_TEXT,
} from "@digits-shared/DesignSystem/Containers/AIGradients"
import { CellText } from "@digits-shared/DesignSystem/Tables/tableStyles"
import { DigitsTooltip } from "@digits-shared/DesignSystem/Tooltip"
import stringHelper from "@digits-shared/helpers/stringHelper"
import { useOnBodyClick } from "@digits-shared/hooks/useOnBodyClick"
import useStateBoolean from "@digits-shared/hooks/useStateBoolean"
import colors from "@digits-shared/themes/colors"
import fonts, { LabelText } from "@digits-shared/themes/typography"
import styled from "styled-components"
import { NewCategoryModal } from "src/frontend/components/OS/Applications/CategoryManager/CategoryPanel/NewCategoryModal/NewCategoryModal"
import { useCategoryManagerPermissions } from "src/frontend/components/OS/Applications/CategoryManager/hooks/useCategoryManagerPermissions"
import { useTextIsOverflowing } from "src/frontend/hooks/useTextIsOverflowing"
import { CategoryHover } from "src/shared/components/CategoryHover/CategoryHover"
import { SVGIconComponent } from "src/shared/components/Icons/SVGIcon"
import {
  type CategoryTypeaheadResult,
  isNewEntityId,
  type NewCategoryEntity,
  type NewResultBehavior,
} from "src/shared/components/Typeahead/sharedTypeahead"
import { Typeahead } from "src/shared/components/Typeahead/Typeahead"
import { useCategoryTooltipContent } from "src/shared/components/Typeahead/useTooltipCategoryContent"
import { useTypeaheadState } from "src/shared/components/Typeahead/useTypeaheadState"

const SelectArrow = styled(SvgChevronDown)`
  position: absolute;
  right: 6px;
  width: 24px;
  height: 24px;
  ${svgPathStyles("var(--category-typeahead-text-muted-color)", 1.5)};

  .with-suggestion & {
    ${svgPathStyles("#5e5be9", 1.5)};
  }
`

const PlaceholderIcon = styled(SvgTag01)`
  height: var(--category-typeahead-icon-size);
  min-width: var(--category-typeahead-icon-size);
  width: var(--category-typeahead-icon-size);
  ${svgPathStyles("var(--category-typeahead-text-muted-color)", 1.5)};
`

const CategoryIcon = styled(SVGIconComponent)`
  height: var(--category-typeahead-icon-size);
  min-width: var(--category-typeahead-icon-size);
  width: var(--category-typeahead-icon-size);
  padding: 2px;
  ${svgPathStyles("var(--category-typeahead-text-color)", 1.5)};

  .with-suggestion & {
    ${svgPathStyles("#399CD4", 2)};
  }
`

const Star = styled(SvgAIFillGradient)`
  position: absolute;
  right: 28px;
  width: 20px;
  height: 20px;
  min-width: 20px;
`

const SelectContainer = styled.div`
  --category-typeahead-text-muted-color: ${colors.secondary50};
  --category-typeahead-text-color: ${colors.secondary};
  --category-typeahead-text-hover-color: ${colors.secondary};
  --category-typeahead-border-color: ${colors.secondary30};
  --category-typeahead-border-hover-color: ${colors.secondary50};
  --category-typeahead-background-color: ${colors.translucentWhite50};
  --category-typeahead-background-hover-color: ${colors.white};
  --category-typeahead-background-disabled-color: ${colors.secondary05};
  --category-typeahead-focus-color: ${colors.accentBlue};
  --category-typeahead-error-border-color: ${colors.errorDark};
  --category-typeahead-icon-size: 24px;
  --category-typeahead-input-width: 100%;
  --category-typeahead-input-v-padding: 7px;

  position: relative;
  width: var(--category-typeahead-input-width);
  padding-left: 8px;
  padding-right: 32px;
  padding-top: var(--category-typeahead-input-v-padding);
  padding-bottom: var(--category-typeahead-input-v-padding);

  border: 1px solid var(--category-typeahead-border-color);
  background: var(--category-typeahead-background-color);
  border-radius: 8px;

  transition:
    background 0.2s ease,
    color 0.2s ease,
    border-color 0.2s ease;

  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;

  ${fonts.scale.body};
  text-align: left;

  color: var(--category-typeahead-text-color);
  &.muted {
    color: var(--category-typeahead-text-muted-color);
  }

  &.with-suggestion {
    ${AI_GRADIENT_BORDER};
    ${AI_GRADIENT_TEXT};

    ${LabelText} {
      ${AI_GRADIENT_TEXT};
    }

    box-sizing: border-box;

    &::before {
      margin: -1px;
    }
  }

  &[data-state="closed"][aria-invalid="true"]:not(:hover):not(.disabled) {
    border-color: var(--category-typeahead-error-border-color);
  }

  &.icon-only {
    padding-right: 8px;
    justify-content: flex-start;

    ${SelectArrow} {
      width: 16px;
      height: 16px;
    }
  }

  &:hover:not(.disabled) {
    cursor: pointer;
    color: var(--category-typeahead-text-hover-color);
    background: var(--category-typeahead-background-hover-color);
    border-color: var(--category-typeahead-border-hover-color);

    ${SelectArrow} {
      ${svgPathStyles("var(--category-typeahead-text-color)", 1.5)};
    }

    &.with-suggestion {
      ${AI_GRADIENT_TEXT};

      ${SelectArrow} {
        ${svgPathStyles("#5e5be9", 1.5)};
      }
    }
  }

  &:focus:not(.disabled) {
    outline: 1px solid var(--category-typeahead-focus-color);
  }

  &.disabled {
    pointer-events: none;
    color: var(--category-typeahead-text-muted-color);
    background: var(--category-typeahead-background-disabled-color);
    border-color: var(--category-typeahead-border-color);
    ${CategoryIcon}, ${PlaceholderIcon} {
      ${svgPathStyles("var(--category-typeahead-text-muted-color)", 1.5)};
    }
  }
`

const Details = styled.div`
  display: flex;
  flex-direction: column;
  width: calc(100% - 28px);

  .with-suggestion & {
    width: calc(100% - 52px);
  }
`

const Subtext = styled(LabelText)`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  width: 100%;
`

// Create: create new categories using the NewCategoryModal
// Select: select a new category name without creating the category
// Never: do not allow adding new categories
type AddNewBehavior = "create" | "select" | "never"

// Activate: activate inactive categories
// Select: select inactive categories without activating
type ActivateBehavior = "activate" | "select"

// Always: always show the tooltip on hover
// Overflow: only show the tooltip on hover if the text overflows
// Never: never show the tooltip on hover
type TooltipDisplay = "always" | "overflow" | "never"

type CategoryTypeaheadProps = {
  category: EntityCategory | undefined | null
  onSelect: (c: EntityCategory | null) => void
  onDelete?: () => void
  hideIcons?: boolean
  inputPlaceholder?: string
  disabledInputPlaceholder?: string
  customIcon?: React.ReactNode
  customTooltip?: React.ReactNode
  tooltipDisplay?: TooltipDisplay
  showCategoryHover?: boolean
  disabled?: boolean
  typeaheadWidth?: number
  includedTypes?: CategoryType[]
  includedSubtypes?: CategorySubtype[]
  includedStates?: CategoryState[]
  suggestedCategoryIds?: string[]
  excludedCategoryIds?: string[]
  excludedAncestorIds?: string[]
  enableSelectRootCategory?: boolean
  enableSelectParentCategory?: boolean
  addNewBehavior?: AddNewBehavior
  activateBehavior?: ActivateBehavior
  newResultBehavior?: NewResultBehavior
  subtext?: string
  flatten?: boolean
  invalid?: boolean
  label?: string
  className?: string
  iconOnly?: boolean
}

type CategoryIconProps = {
  category: EntityCategory | undefined | null
  customIcon?: React.ReactNode
  showCategoryHover?: boolean
  hideIcons?: boolean
}

export const CategoryTypeahead: React.FC<CategoryTypeaheadProps> = ({
  category,
  onSelect,
  disabled = false,
  subtext,
  onDelete,
  inputPlaceholder = "Select category…",
  customIcon,
  hideIcons,
  typeaheadWidth,
  includedTypes,
  includedSubtypes,
  includedStates,
  suggestedCategoryIds,
  excludedCategoryIds,
  excludedAncestorIds,
  enableSelectParentCategory,
  enableSelectRootCategory,
  flatten,
  addNewBehavior = "create",
  activateBehavior = "activate",
  newResultBehavior = "search",
  tooltipDisplay = "overflow",
  className,
  invalid,
  label = "Select category",
  customTooltip,
  showCategoryHover = false,
  iconOnly = false,
  disabledInputPlaceholder = "No Category",
}) => {
  const containerRef = React.useRef<HTMLDivElement | null>(null)
  const { hasCreatePermission } = useCategoryManagerPermissions()
  const { isTextOverflowing, textElementRef } = useTextIsOverflowing<HTMLDivElement>()
  const [initialCategory, setInitialCategory] = React.useState<
    Category | NewCategoryEntity | undefined
  >(undefined)

  const { isTypeaheadShown, hideTypeahead, onClick, onKeyDown, ref, defaultSearchTerm } =
    useTypeaheadState(containerRef, disabled, onDelete)

  const showSuggestedStyles = Boolean(
    suggestedCategoryIds?.length && category && suggestedCategoryIds.includes(category.id)
  )

  useOnBodyClick(ref, hideTypeahead)

  const {
    value: isCreateModalOpen,
    setTrue: openCreateModal,
    setFalse: closeCreateModal,
  } = useStateBoolean(false)

  const placeholder = disabled ? disabledInputPlaceholder : inputPlaceholder

  const isTooltipDisabled = React.useMemo(() => {
    // Category hover overrides the tooltip
    if (showCategoryHover) return true

    switch (tooltipDisplay) {
      case "always":
        return isTypeaheadShown || !category
      case "never":
        return true
      case "overflow":
      default:
        return isTypeaheadShown || !category || !isTextOverflowing
    }
  }, [category, isTextOverflowing, isTypeaheadShown, showCategoryHover, tooltipDisplay])

  const onResultSelected = React.useCallback(
    (c: CategoryTypeaheadResult) => {
      if (containerRef.current) {
        containerRef.current.focus()
      }

      // If we have a new entity id, open the new category modal
      if (isNewEntityId(c.entity.id) && hasCreatePermission) {
        if (addNewBehavior === "select") {
          onSelect(c.entity)
        } else {
          setInitialCategory(c.entity)
          openCreateModal()
        }
        hideTypeahead()
        return
      }

      // If a category has the "new" property, it is inactive
      // We still show the new modal to allow them to make adjustments
      if (c.isNew && hasCreatePermission) {
        if (activateBehavior === "select") {
          onSelect(c.entity)
        } else {
          setInitialCategory(c.entity as unknown as Category)
          openCreateModal()
        }
        hideTypeahead()
        return
      }

      // Otherwise, set the category and hide the typeahead
      onSelect(c.entity)
      hideTypeahead()
    },
    [
      hasCreatePermission,
      onSelect,
      hideTypeahead,
      addNewBehavior,
      openCreateModal,
      activateBehavior,
    ]
  )

  const onCreateNew = React.useCallback(
    (entity: EntityCategory) => {
      onSelect(entity)
    },
    [onSelect]
  )

  const tooltipContent = useCategoryTooltipContent({
    category,
    customTooltip,
    disabled: isTooltipDisabled,
  })

  const classNames = stringHelper.classNames(
    className,
    showSuggestedStyles && "with-suggestion",
    disabled && "disabled",
    !category && "muted",
    iconOnly && "icon-only"
  )

  return (
    <>
      <DigitsTooltip disabled={isTooltipDisabled} content={tooltipContent} maxWidth={300}>
        <SelectContainer
          ref={containerRef}
          className={classNames}
          aria-invalid={invalid}
          onClick={onClick}
          onKeyDown={onKeyDown}
          tabIndex={0}
          aria-disabled={disabled ? true : undefined}
          data-state={isTypeaheadShown ? "open" : "closed"}
          role="combobox"
          aria-label={label}
        >
          <TypeaheadIconWithHover
            category={category}
            hideIcons={hideIcons}
            customIcon={customIcon}
            showCategoryHover={showCategoryHover}
          />

          {!iconOnly && (
            <Details className="categoryDetails">
              <CellText ref={textElementRef} data-placeholder={category?.name ? undefined : true}>
                {category?.name || placeholder}
              </CellText>
              {subtext && <Subtext>{subtext}</Subtext>}
            </Details>
          )}

          {showSuggestedStyles && !!suggestedCategoryIds?.length && (
            <Star IconComponent={SvgStars01Solid} />
          )}

          {!disabled && <SelectArrow className="selectArrow" />}

          {isTypeaheadShown && (
            <Typeahead
              ref={ref}
              kind={ObjectKind.Category}
              onResultSelected={onResultSelected}
              currentValue={category?.id}
              close={hideTypeahead}
              includedTypes={includedTypes}
              includedSubtypes={includedSubtypes}
              includedStates={includedStates}
              suggestedCategoryIds={suggestedCategoryIds}
              excludedCategoryIds={excludedCategoryIds}
              excludedAncestorIds={excludedAncestorIds}
              includedCategoryIds={category ? [category.id] : undefined}
              enableSelectRootCategory={enableSelectRootCategory}
              enableSelectParentCategory={enableSelectParentCategory}
              newResultBehavior={newResultBehavior}
              onClear={onDelete}
              placeholder={addNewBehavior !== "never" ? "Select or Create New" : "Find Category"}
              allowAddNew={addNewBehavior !== "never" && hasCreatePermission}
              flatten={flatten}
              width={typeaheadWidth}
              defaultSearchTerm={defaultSearchTerm}
            />
          )}
        </SelectContainer>
      </DigitsTooltip>

      {isCreateModalOpen && (
        <NewCategoryModal
          initialCategory={initialCategory}
          onCreateNew={onCreateNew}
          onClose={closeCreateModal}
        />
      )}
    </>
  )
}

const TypeaheadIcon: React.FC<CategoryIconProps> = ({ category, customIcon }) => {
  if (customIcon) return customIcon

  return category ? (
    <CategoryIcon
      subjectDisplayKey={category.displayKey || category.name}
      className="categoryIcon"
    />
  ) : (
    <PlaceholderIcon className="categoryPlaceholderIcon" />
  )
}

const TypeaheadIconWithHover: React.FC<CategoryIconProps> = ({
  category,
  hideIcons,
  customIcon,
  showCategoryHover,
}) => {
  if (!category || hideIcons) return null
  if (!showCategoryHover) return <TypeaheadIcon category={category} customIcon={customIcon} />

  return (
    <CategoryHover
      category={category}
      triggerContent={<TypeaheadIcon category={category} customIcon={customIcon} />}
    />
  )
}
